import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Button, ButtonProps } from '@praetorian-chariot/ui';
import React, {
  Fragment,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';

import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';
import { getTransitionSettings } from '@/utils/transition.util';

type Size = 'xs' | 'lg' | 'xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full';
export interface ModalProps extends PropsWithChildren {
  className?: string;
  baseClassName?: string;
  bodyClassName?: string;
  hideFooter?: boolean;
  footer?: {
    baseClassName?: string;
    styleType?: ButtonProps['styleType'];
    text?: string;
    onClick?: () => void;
    left?: React.ReactElement;
    form?: string;
    className?: string;
    isLoading?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    disabled?: boolean;
    'data-testid'?: string;
    disconnect?: {
      text?: string;
      onClick?: () => void;
      disabled?: boolean;
      isLoading?: boolean;
    };
    hideCancelButton?: boolean;
  };
  logo?: string;
  icon?: React.ReactNode;
  onClose: () => void;
  hideClose?: boolean;
  open: boolean;
  size?: Size;
  title: ReactNode;
  subtitle?: ReactNode;
  style?: 'default' | 'dialog';
  closeOnOutsideClick?: boolean;
  divider?: boolean;
  disableAutoFocus?: boolean;
}

/**
 * This is Portal ID where headless UI renders the Dialog
 * Ref: https://headlessui.com/react/dialog#rendering-to-a-portal
 */
export const MODAL_WRAPPER_ID = 'headlessui-portal-root';

export const Modal: React.FC<ModalProps> = props => {
  const {
    children,
    className = '',
    baseClassName = '',
    footer = undefined,
    onClose = () => {},
    divider = false,
    logo = '',
    subtitle = '',
    open = false,
    size = 'lg',
    title = '',
    icon,
    style = 'default',
    closeOnOutsideClick = true,
    hideFooter,
    bodyClassName = '',
    hideClose,
    disableAutoFocus = false,
  } = props;
  const { modal } = useGlobalState();
  const { onOpenChange: setModalOpen } = modal.allModal;
  const isDialog = style === 'dialog';
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const footerButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      event.stopPropagation();

      if (event.key === 'Escape') {
        onClose();
      }
    }

    if (open) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [open]);

  useEffect(() => {
    setModalOpen(open);
  }, [open]);

  useEffect(() => {
    if (open && footer?.text && !disableAutoFocus) {
      const timeoutId = setTimeout(() => {
        footerButtonRef.current?.focus();
      }, 100);
      return () => {
        clearTimeout(timeoutId);
        footerButtonRef.current?.blur();
      };
    }
  }, [open, disableAutoFocus]);

  return (
    <ModalWrapper
      open={open}
      size={size}
      onClose={onClose}
      closeOnOutsideClick={closeOnOutsideClick}
      className={baseClassName}
    >
      <div
        data-testid="modal"
        className={cn('flex size-full flex-col', bodyClassName)}
      >
        <div>
          <Dialog.Title
            className={cn(
              'flex justify-between rounded-t-lg text-lg font-semibold',
              !isDialog && 'bg-chariot-background-primary py-2 pl-6'
            )}
            data-testid="modal-title"
          >
            <div className="flex w-full items-center justify-center">
              {logo && (
                <img
                  src={logo}
                  alt={'modal-logo'}
                  className="mr-2 inline size-12"
                />
              )}
              {icon && (
                <div className="flex size-10 shrink-0 items-center justify-center">
                  {icon}
                </div>
              )}
              <div className="w-full">
                {title}
                {subtitle && (
                  <p className="text-chariot-text-secondary text-sm">
                    {subtitle}
                  </p>
                )}
              </div>
            </div>

            {!hideClose && (
              <Button
                startIcon={<XMarkIcon className="size-6" />}
                onClick={onClose}
                styleType="icon"
                label=""
                data-testid="modal-close-button"
              />
            )}
          </Dialog.Title>
          {divider && (
            <div className="w-full px-6 py-2">
              <div className="bg-chariot-stroke h-px w-full" />
            </div>
          )}
          <div
            className={cn(
              'size-full overflow-auto px-6 py-2',
              isDialog && 'px-10 py-2',
              isDialog && !icon && 'px-0',
              className
            )}
            data-testid="modal-body"
          >
            {children}
          </div>
        </div>
        {!hideFooter && (
          <div
            className={cn(
              'flex justify-between rounded-b-lg',
              !isDialog && 'px-6 py-4 pb-5',
              isDialog && 'p-6',
              footer?.baseClassName
            )}
          >
            <div
              className={cn(
                confirmDisconnect
                  ? 'w-[460px]'
                  : title == 'Proof of Exploit'
                    ? 'w-full'
                    : 'w-[260px]'
              )}
            >
              {footer?.left}
              {confirmDisconnect ? (
                <div className="flex items-center space-x-2">
                  <span className="flex-1">
                    Are you sure you want to disconnect?
                  </span>

                  <Button
                    onClick={() => setConfirmDisconnect(false)}
                    styleType="secondary"
                    className={cn(footer?.className, 'px-4')}
                    label="Cancel"
                    size="xs"
                  />
                  <Button
                    onClick={() => {
                      footer?.disconnect?.onClick?.();
                      setConfirmDisconnect(false);
                    }}
                    styleType="secondary"
                    className={cn(
                      'hover:bg-chariot-back border-red-700 text-red-700',
                      footer?.className
                    )}
                    size="xs"
                    label="Yes"
                  />
                </div>
              ) : (
                footer?.disconnect && (
                  <Button
                    onClick={() => setConfirmDisconnect(true)}
                    styleType="secondary"
                    className={cn(
                      'hover:bg-chariot-back text-red-700',
                      footer?.className
                    )}
                    disabled={footer?.disconnect?.disabled}
                    label={footer?.disconnect?.text || ''}
                    size="xs"
                  />
                )
              )}
            </div>
            {!confirmDisconnect && (
              <div className="flex gap-2">
                {!footer?.hideCancelButton ? (
                  <Button
                    onClick={onClose}
                    styleType="tertiary"
                    className="bg-chariot-background-primary m-0! px-4"
                    label="Cancel"
                    size="md"
                    data-testid="modal-cancel-button"
                  />
                ) : (
                  <div></div>
                )}

                {footer?.text && (
                  <Button
                    ref={footerButtonRef}
                    onClick={() => footer?.onClick?.()}
                    startIcon={footer?.startIcon}
                    endIcon={footer?.endIcon}
                    styleType={footer.styleType || 'primary'}
                    type={footer?.form ? 'submit' : undefined}
                    form={footer?.form}
                    className={cn('ml-2 min-w-24 px-4', footer?.className)}
                    label={footer.text || ''}
                    disabled={footer?.disabled}
                    size="md"
                    data-testid={
                      footer['data-testid'] || 'modal-primary-button'
                    }
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </ModalWrapper>
  );
};

interface ModalWrapperProps extends PropsWithChildren {
  className?: string;
  open: boolean;
  size?: Size;
  onClose: () => void;
  closeOnOutsideClick?: boolean;
}

export const ModalWrapper: React.FC<ModalWrapperProps> = props => {
  const {
    children,
    className = '',
    onClose = () => {},
    open = false,
    size = 'lg',
    closeOnOutsideClick = true,
  } = props;

  const widthMap = {
    xs: 'max-w-xs',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    full: 'w-[95%] h-[95%]',
  };

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[150] w-full"
        onClose={closeOnOutsideClick ? onClose : () => null}
      >
        <Transition.Child
          as={Fragment}
          {...getTransitionSettings({ type: 'fade' })}
        >
          <div
            className="dark:bg-chariot-background-primary/60 fixed inset-0 bg-black/25" // TODO: Update to chariot colors when it start supporting alpha values
            aria-hidden="true"
          />
        </Transition.Child>

        <div className="fixed inset-0 flex w-full justify-center overflow-hidden">
          <div className="text-chariot-text-primary flex min-h-full w-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              {...getTransitionSettings({ type: 'scale' })}
            >
              <Dialog.Panel
                className={cn(
                  `border-chariot-stroke w-full border-2 ${widthMap[size]} bg-chariot-background-primary max-h-full overflow-auto rounded-[10px] text-left transition-all`,
                  className
                )}
              >
                <div className="size-full">{children}</div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};
